<template>
  <div id="container">
    <TheNavbar />
    <hr class="mt-0 mb-0" />
    
    <div class="accordion-container">
      <div class="title">Geophysical Imaging</div>
      
      <div class="accordion-section collapsed" id="theory-section">
        <div class="accordion-header" @click="expandSection('theory')">
          <span class="triangle">&#9654;</span>
          <span class="title">Theoretical Foundation</span>
        </div>
        <div class="accordion-content" style="display:none;">
          <p>Geophysical imaging is grounded in principles from several areas of physics:</p>
          <ul>
            <li><b>Seismic Theory</b>: Explores how elastic waves move through Earth materials. Useful for detecting subsurface structures based on wave reflections and refractions.</li>
            <li><b>Electromagnetic Theory</b>: Studies how electromagnetic fields interact with subsurface materials. Foundational for methods like Ground Penetrating Radar (GPR) and magnetotellurics.</li>
            <li><b>Ohm's Law & Maxwell's Equations</b>: Underpin electrical resistivity and induced polarization methods by modeling how currents flow underground.</li>
            <li><b>Potential Field Theory</b>: Helps analyze variations in Earth's gravitational and magnetic fields caused by different materials underground.</li>
            <li><b>Inverse Problem Theory</b>: Mathematical techniques that reconstruct images of the subsurface from indirect physical measurements.</li>
          </ul>
        </div>
      </div>
      
      <div class="accordion-section collapsed" id="equipment-section">
        <div class="accordion-header" @click="expandSection('equipment')">
          <span class="triangle">&#9654;</span>
          <span class="title">Equipment</span>
        </div>
        <div class="accordion-content" style="display:none;">
          <p>Each geophysical method uses specialized tools:</p>
          <ul>
            <li><b>Seismic Methods</b>: Geophones / Hydrophones, Seismographs, Seismic sources (vibroseis trucks, hammers, explosives)</li>
            <li><b>Electrical & EM Methods</b>: Resistivity meters, Induced polarization (IP) equipment, Electromagnetic conductivity meters (e.g., EM31, EM38), GPR units</li>
            <li><b>Gravity & Magnetic Methods</b>: Gravimeters, Magnetometers</li>
            <li><b>Additional Tools</b>: LIDAR (for topography), Drones/UAVs (for aerial surveys)</li>
          </ul>
        </div>
      </div>
      
      <div class="accordion-section collapsed" id="applications-section">
        <div class="accordion-header" @click="expandSection('applications')">
          <span class="triangle">&#9654;</span>
          <span class="title">Applications</span>
        </div>
        <div class="accordion-content" style="display:none;">
          <ul>
            <li><b>Environmental & Engineering</b>: Site assessments for construction, Detecting buried tanks, pipes, or contamination, Assessing infrastructure stability</li>
            <li><b>Energy & Resources</b>: Exploration of oil, gas, minerals, and geothermal energy, Monitoring subsurface reservoir changes</li>
            <li><b>Archaeology</b>: Locating ancient structures and graves, Non-invasive site mapping</li>
            <li><b>Natural Hazard Assessment</b>: Mapping fault lines and earthquake-prone zones, Volcano monitoring, Sinkhole and landslide detection</li>
            <li><b>Water & Hydrology</b>: Groundwater and aquifer mapping, Tracking salinity and water table variations</li>
            <li><b>Civil Infrastructure</b>: Detecting subsurface voids and tunnels, Evaluating roads, dams, and foundations</li>
          </ul>
        </div>
      </div>
    </div>
    
    <!-- <TheFooter /> -->
  </div>
</template>

<script>
import TheNavbar from '@/components/TheNavbar.vue'
import TheFooter from '@/components/TheFooter.vue'

export default {
  name: 'Geophysical',
  components: {
    TheNavbar,
    TheFooter
  },
  data() {
    return {
      activeSection: null
    }
  },
  methods: {
    expandSection(section) {
      const current = document.getElementById(section + '-section');
      const isExpanded = current.classList.contains('expanded');
      
      // 先收起所有
      document.querySelectorAll('.accordion-section').forEach(sec => {
        sec.classList.add('collapsed');
        sec.classList.remove('expanded');
        sec.querySelector('.accordion-content').style.display = 'none';
        sec.querySelector('.triangle').innerHTML = '&#9654;'; // ▶
      });
      
      // 如果当前是收起的，则展开；如果是展开的，则保持收起
      if (!isExpanded) {
        current.classList.remove('collapsed');
        current.classList.add('expanded');
        current.querySelector('.accordion-content').style.display = '';
        current.querySelector('.triangle').innerHTML = '&#9660;'; // ▼
      }
    }
  }
}
</script>

<style scoped>
.accordion-container {
  padding: 32px;
  max-width: 1200px;
  width: 100%;
  margin: 32px auto;
  background: #f8fafc;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(60, 80, 120, 0.08);
  color: #2A439A;
  font-family: 'Segoe UI', 'Arial', sans-serif;
}

.accordion-container > .title {
  font-size: 2.3em;
  font-weight: 700;
  text-align: center;
  margin-bottom: 32px;
  color: #2A439A;
  letter-spacing: 1px;
}

.accordion-section {
  margin-bottom: 16px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(60, 80, 120, 0.04);
  border: 1px solid #e3e8f0;
  transition: box-shadow 0.2s;
}

.accordion-section.expanded {
  box-shadow: 0 4px 16px rgba(60, 80, 120, 0.10);
  border-color: #2A439A;
}

.accordion-header {
  cursor: pointer;
  padding: 18px 28px;
  font-size: 1.25em;
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #2A439A;
  border-radius: 10px 10px 0 0;
  background: #eee;
  transition: background 0.2s;
}

.accordion-header:hover {
  background: #ddd;
}

.accordion-header .triangle {
  margin-right: 14px;
  font-size: 1.3em;
  transition: transform 0.2s;
}

.accordion-section.expanded .accordion-header .triangle {
  transform: rotate(90deg);
}

.accordion-header .title {
  font-size: 1.2em;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.accordion-content {
  padding: 18px 32px 18px 48px;
  background: #f5faff;
  border-radius: 0 0 10px 10px;
  color: #263238;
  font-size: 1.08em;
  line-height: 1.7;
  border-top: 1px solid #e3e8f0;
}

.accordion-content ul {
  margin: 0 0 0 1.2em;
  padding: 0;
}

.accordion-content li {
  margin-bottom: 0.7em;
}
</style>






